import { Logger }    from '@/util/logger';
import { Node }      from './node';
import { Tree }      from './tree';
import {
  Buffer,
  Path
}                    from './e.m.c.a.404.types';
import { Listener }  from './e.m.c.a.404.events';

/**
 *
 * @class EventTree
 *
 * The controller for the path tree.  Provides the ability register listeners on a "path"
 * in the JSON.  A path is just the node names in sequence, from the root down to a node of
 * interest. Because JSON is tree-like, if our paths are modeled as a tree, then it will
 * essentially mirror the JSON structure, and as you parse the JSON you can just adjust
 * a cursor in the path tree to align with where you are in JSON parsing.
 *
 * Then to check if the application is listening on that part of the JSON, just check for
 * listeners on the current node in the path tree.  Of course, if the path tree
 * structure isn't the same as the JSON...then you won't get any matches.  So its
 * assumed that the application  knows the JSON structure in advance, and is providing
 * paths that do match the JSON structure.
 *
 * To allow for basic globbing, any component of the "path" can be a '*' and
 * when we do path matching we just "expand" the path tree to a version where we
 * copy the listeners on '*' nodes, to the peer nodes (same level) which are not
 * '*' nodes.  That ensures the matching by name will hit the listeners that were
 * registered with a '*'.
 *
 * More sophisticated matching could be done, use regex etc.
 *
 * Paths provide two key benefits:
 *
 *   - when parsing, if we're at a JSON node that doesn't have an associated listener
 *     then we can "ignore" that node, we don't have to fully parse it, we can just skip
 *     to the next thing.  This gives a nice performance boost :)
 *
 *   - the application has a simple model for gathering data from the JSON, provide a path
 *     and a callback...a "listener", and when that node of the JSON is hit, the callback
 *     will be invoked with the JSON data.  Listen to as much or as little as desired.
 *
 * The JSON structure is tree-like, so a tree is used to mirror the JSON structure, and a
 * path is just the node names for some "path" through the tree.
 *
 * Because we allow for basic globbing; the path can include one or more '*' characters,
 * everytime a new listener is registered, we (re)clone the path tree and expand the wildcard
 * node...which is basically just copying the listeners from the wildcard nodes to the
 * peers that aren't wildcard nodes.  That's how we ensure wild card matching happens.  It
 * just means that we have to do our path matching...on the wildcard expanded path tree, not
 * the actual path tree.
 *
 * Some ideas for the parsing taken from:
 *
 *   - https://github.com/alemures/fast-json
 *   - https://datastation.multiprocess.io/blog/2022-01-06-analyzing-large-json-files-via-partial-json-parsing.html
 *   - https://github.com/uhop/stream-json
 *   - https://github.com/Collaborne/incremental-json-parser
 *   - https://djedr.github.io/posts/jsonhilo-2021-07-29.html
 *   - https://github.com/xtao-org/jsonhilo
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      May 2023
 *
 */
export class EventTree {

  /** the path tree; if not similar to actual JSON structure, it won't match anything :) */
  private tree: Tree;

  /** the path separator */
  private readonly separator: string = '/';

  /** the wildcard expanded version of the tree */
  private expandedTree: Tree;

  /** the "cursor" for where we are at currently in the JSON */
  private node: Node|undefined;

  /** standard constructor */
  constructor() {

    this.separator    = '/';
    this.tree         = new Tree(this.separator);
    this.expandedTree = this.tree.getCloneExpanded();

    /* match on the expanded tree */

    this.node         = this.expandedTree.root;
  }

  /** when we open a new node in the JSON, we have to move down into it */
  down(name: string) {

    name = name.replaceAll('"', '');

    if (name !== this.expandedTree.root.name) {

      if (this.node !== undefined) {

        const node: Node | undefined = this.node.child(name) ?? this.node.child('*');

        if (node === undefined) {

          throw new Error('Could not get child');
        }

        this.node = node;
      }
    }
  }

  /** when we complete a node in the JSON, we have to move back up to the parent */
  up() {

    if (this.node !== undefined) {

      if (this.node.parent !== undefined) {

        this.node = this.node.parent;
      }
    }
  }

  /** check if this node exists in the tree */
  hasNode(name: string): boolean {

    name = name.replaceAll('"', '');

    if (name === this.expandedTree.root.name) {

      return true;
    }

    if (this.node === undefined) {

      return false;
    }

    return (this.node.child(name) !== undefined || this.node.child('*') !== undefined);
  }

  /** check if the current node has any listeners */
  hasListener(): boolean {

    if (this.node === undefined) {

      return false;
    }

    return this.node.hasListeners();
  }

  /** invoke the listeners for the current node */
  emit(data: Buffer, index: string|number|undefined): void {

    if (typeof index === 'string') {

      index = index.replaceAll('"', '');
    }

    if (this.node === undefined) {

      return ;
    }

    this.node.listen(data, index);
  }

  /**
   * register a callback to invoke when this part of the JSON is parsed.
   * for example:
   *
   *   *.people[*].name
   *
   * Will match anything that has a 'people' filed in an  object, and
   * then the 'name' field of any objects of that array.  You can register
   * as many paths as you like.
   *
   * @param path      the JSON path to listen on
   * @param listener  the callback when that path is hit
   *
   */
  on(path: string, listener: Listener) {

    if (!this.node) {

      Logger.error(`[EventTree] can not register JSON handler, no tree yet.`);

      return ;
    }

    /* add this path to the tree */

    const pathArr = this.parsePath(path);
    this.tree.add(pathArr, listener);

    /* recreate teh expanded version so the wildcards are matching actual nodes */

    const nodePath: Path = this.node.getPath();
    this.expandedTree    = this.tree.getCloneExpanded();
    this.node            = this.expandedTree.get(nodePath);
  }

  /** move the cursor back to the top of the tree */
  reset() {

    this.node = this.expandedTree.root;
  }

  private parsePath(origPath: string): Path {

    if (origPath.length === 0) {

      return [];
    }

    /*
     * we want to all a '*' to be a component in the path, but when
     * they use array indexing like .../[*]/... we don't want the
     * brackets. Node names for elements of an array...will just be
     * the index number for the array.  Not "[1]" etc.
     *
     * Just remove any brackets in the path.
     *
     */

    let path = origPath.replace(/[\/[]/g, this.separator);

    path = path.replace(/]/g, '');
    path = path.startsWith(this.separator) ? path.substring(this.separator.length) : path;

    return path.split(this.separator);
  }
}
